<template>
  <div class="admin-layout-container" :class="{ 'layout-mode-app': layoutMode === 'app' }">
    <!-- Sidebar Navigation -->
    <aside 
      class="admin-sidebar" 
      :class="{ 'sidebar-collapsed': isSidebarCollapsed }"
    >
      <!-- Brand Logo Header -->
      <div class="sidebar-brand d-flex justify-content-between align-items-center">
        <div class="d-flex align-items-center gap-2">
          <icon icon="fa-solid fa-book-open" class="brand-icon" />
          <span class="brand-text font-serif">Lối Đọc <span class="badge-admin">Admin</span></span>
        </div>
        <button 
          class="sidebar-close-btn btn d-lg-none" 
          @click="closeSidebar"
          aria-label="Close Navigation"
        >
          <icon icon="fa-solid fa-xmark" />
        </button>
      </div>

      <!-- Navigation Links -->
      <nav class="sidebar-nav">
        <ul class="nav-list">
          <li class="nav-item" v-if="false">
            <router-link to="/management/dashboard" class="nav-link" active-class="active" data-tooltip="Tổng quan">
              <icon icon="fa-solid fa-chart-line" class="nav-icon" />
              <span>Tổng quan</span>
            </router-link>
          </li>
          
          <li class="nav-item">
            <router-link to="/management/books" class="nav-link" active-class="active" data-tooltip="Kho sách">
              <icon icon="fa-solid fa-book" class="nav-icon" />
              <span>Kho sách</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/management/authors" class="nav-link" active-class="active" data-tooltip="Quản lý tác giả">
              <icon icon="fa-solid fa-user-pen" class="nav-icon" />
              <span>Quản lý tác giả</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/management/translators" class="nav-link" active-class="active" data-tooltip="Quản lý dịch giả">
              <icon icon="fa-solid fa-language" class="nav-icon" />
              <span>Quản lý dịch giả</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/management/categories" class="nav-link" active-class="active" data-tooltip="Quản lý thể loại">
              <icon icon="fa-solid fa-tags" class="nav-icon" />
              <span>Quản lý thể loại</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/management/book-suggests" class="nav-link" active-class="active" data-tooltip="Đề xuất sách">
              <icon icon="fa-solid fa-paper-plane" class="nav-icon" />
              <span>Đề xuất sách</span>
            </router-link>
          </li>

          <li class="nav-item">
            <router-link to="/management/approvals" class="nav-link" active-class="active" :data-tooltip="isAdmin ? 'Phê duyệt yêu cầu' : 'Yêu cầu của tôi'">
              <icon icon="fa-solid fa-clipboard-check" class="nav-icon text-warning" />
              <span class="text-warning">{{ isAdmin ? 'Phê duyệt yêu cầu' : 'Yêu cầu của tôi' }}</span>
            </router-link>
          </li>
        </ul>
      </nav>
    </aside>

    <!-- Overlay backdrop for mobile -->
    <div 
      v-if="!isSidebarCollapsed" 
      class="sidebar-backdrop d-lg-none" 
      @click="closeSidebar"
    ></div>

    <!-- Main Workspace Container -->
    <div class="admin-main-workspace">
      <!-- Admin Top Nav Bar -->
      <header class="admin-topbar">
        <div class="topbar-left d-flex align-items-center">
          <button 
            v-if="layoutMode !== 'app'"
            class="topbar-sidebar-toggle btn me-3" 
            @click="toggleSidebar"
            aria-label="Toggle Navigation"
          >
            <icon icon="fa-solid fa-bars" />
          </button>
          <h2 class="topbar-title font-serif">{{ pageTitle }}</h2>
        </div>
        
        <div class="topbar-right">
          <!-- Profile Dropdown Container -->
          <div class="admin-profile-dropdown-container">
            <button 
              @click="toggleProfileDropdown($event)"
              class="admin-profile-badge-trigger"
              aria-label="User Menu"
            >
              <div class="admin-avatar-mini">
                <icon icon="fa-solid fa-circle-user" />
              </div>
              <span class="admin-badge-label d-none d-sm-inline">{{ displayName }}</span>
              <icon icon="fa-solid fa-chevron-down" class="dropdown-arrow-icon" />
            </button>

            <!-- Profile Dropdown Menu -->
            <transition name="slide-fade">
              <div v-if="showProfileDropdown" class="profile-dropdown-menu shadow-lg">
                <div class="dropdown-user-details">
                  <h4 class="dropdown-user-name">{{ displayName }}</h4>
                  <span class="dropdown-user-role">{{ userRoleLabel }}</span>
                </div>
                <div class="dropdown-divider"></div>
                <button v-if="isMobileOrTablet" @click="toggleLayoutMode" class="dropdown-layout-toggle-btn">
                  <icon :icon="layoutMode === 'app' ? 'fa-solid fa-desktop' : 'fa-solid fa-mobile-screen'" class="me-2" />
                  <span>{{ layoutMode === 'app' ? 'Giao diện Web' : 'Giao diện App' }}</span>
                </button>
                <button @click="handleLogout" class="dropdown-logout-btn">
                  <icon icon="fa-solid fa-right-from-bracket" class="me-2" />
                  <span>Đăng xuất</span>
                </button>
              </div>
            </transition>
          </div>
        </div>
      </header>
 
      <!-- Sub Route Render Space -->
      <main class="admin-viewport-content">
        <router-view />
      </main>
    </div>

    <!-- Bottom Nav Bar for Mobile App Mode (under 768px) -->
    <nav v-if="layoutMode === 'app'" class="admin-bottom-nav d-md-none">
      <router-link to="/management/dashboard" class="bottom-nav-item" active-class="active" v-if="false">
        <icon icon="fa-solid fa-chart-line" class="bottom-nav-icon" />
        <span>Tổng quan</span>
      </router-link>
      <router-link to="/management/books" class="bottom-nav-item" active-class="active">
        <icon icon="fa-solid fa-book" class="bottom-nav-icon" />
        <span>Kho sách</span>
      </router-link>
      <router-link to="/management/authors" class="bottom-nav-item" active-class="active">
        <icon icon="fa-solid fa-user-pen" class="bottom-nav-icon" />
        <span>Tác giả</span>
      </router-link>
      <router-link to="/management/translators" class="bottom-nav-item" active-class="active">
        <icon icon="fa-solid fa-language" class="bottom-nav-icon" />
        <span>Dịch giả</span>
      </router-link>
      <router-link to="/management/categories" class="bottom-nav-item" active-class="active">
        <icon icon="fa-solid fa-tags" class="bottom-nav-icon" />
        <span>Thể loại</span>
      </router-link>
      <router-link to="/management/book-suggests" class="bottom-nav-item" active-class="active">
        <icon icon="fa-solid fa-paper-plane" class="bottom-nav-icon" />
        <span>Đề xuất</span>
      </router-link>
      <router-link to="/management/approvals" class="bottom-nav-item" active-class="active" v-if="isAdmin">
        <icon icon="fa-solid fa-clipboard-check" class="bottom-nav-icon text-warning" />
        <span class="text-warning">Duyệt xóa</span>
      </router-link>
    </nav>

    <!-- Left Navigation Rail for iPad App Mode (>= 768px) -->
    <nav v-if="layoutMode === 'app'" class="admin-nav-rail d-none d-md-flex">
      <router-link to="/management/dashboard" class="rail-nav-item" active-class="active" data-tooltip="Tổng quan" v-if="false">
        <icon icon="fa-solid fa-chart-line" class="rail-nav-icon" />
        <span>Tổng quan</span>
      </router-link>
      <router-link to="/management/books" class="rail-nav-item" active-class="active" data-tooltip="Kho sách">
        <icon icon="fa-solid fa-book" class="rail-nav-icon" />
        <span>Kho sách</span>
      </router-link>
      <router-link to="/management/authors" class="rail-nav-item" active-class="active" data-tooltip="Tác giả">
        <icon icon="fa-solid fa-user-pen" class="rail-nav-icon" />
        <span>Tác giả</span>
      </router-link>
      <router-link to="/management/translators" class="rail-nav-item" active-class="active" data-tooltip="Dịch giả">
        <icon icon="fa-solid fa-language" class="rail-nav-icon" />
        <span>Dịch giả</span>
      </router-link>
      <router-link to="/management/categories" class="rail-nav-item" active-class="active" data-tooltip="Thể loại">
        <icon icon="fa-solid fa-tags" class="rail-nav-icon" />
        <span>Thể loại</span>
      </router-link>
      <router-link to="/management/book-suggests" class="rail-nav-item" active-class="active" data-tooltip="Đề xuất">
        <icon icon="fa-solid fa-paper-plane" class="rail-nav-icon" />
        <span>Đề xuất</span>
      </router-link>
      <router-link to="/management/approvals" class="rail-nav-item" active-class="active" :data-tooltip="isAdmin ? 'Phê duyệt yêu cầu' : 'Yêu cầu của tôi'">
        <icon icon="fa-solid fa-clipboard-check" class="rail-nav-icon text-warning" />
        <span class="text-warning">{{ isAdmin ? 'Phê duyệt' : 'Yêu cầu' }}</span>
      </router-link>
    </nav>
  </div>
</template>

<script src="./admin-layout.js"></script>
<style scoped src="./admin-layout.css"></style>
